import { Injectable, NgZone, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ENUM_ActivityEventTrafficSource } from '@api-new/common';
import { ConsentStatus, ConsentType, FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { forceSentAnalyticsEventsInBuffer } from '@client-ui/store/analytics/analytics.actions';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { initializeApp } from 'firebase/app';
import { fromEvent, merge } from 'rxjs';
import { filter, take, throttleTime } from 'rxjs/operators';
import * as uuid from 'uuid';

const SESSION_ID_KEY = 'SESSION_ID';
const SESSION_IS_FIRST_SIGN_IN = 'SESSION_IS_FIRST_SIGN_IN';
const SESSION_TRAFFIC_SOURCE = 'SESSION_TRAFFIC_SOURCE';
const LAST_ACTIVITY_KEY = 'LAST_ACTIVITY';
const SESSION_TIMEOUT_MS = 1800000; // 30 minutes

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private activatedRoute = inject(ActivatedRoute);
  private store = inject(Store);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  private enableAnalytics = false;
  private utmParams: Record<string, string> = {};

  constructor() {
    if (!environment.mobileApp?.isMobileApp) initializeApp(environment.firebaseConfig);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => {
        this.initTracking();
      });
  }

  enableAnalyticsCollection(): void {
    this.enableAnalytics = true;
    FirebaseAnalytics.setEnabled({ enabled: true });
    FirebaseAnalytics.setConsent({
      type: ConsentType.AnalyticsStorage,
      status: ConsentStatus.Granted,
    });
    FirebaseAnalytics.setConsent({
      type: ConsentType.AdStorage,
      status: ConsentStatus.Granted,
    });
    FirebaseAnalytics.setConsent({
      type: ConsentType.AdUserData,
      status: ConsentStatus.Granted,
    });
  }

  initTracking(): void {
    this.initializeSession();
    this.registerListenerToSendFinalEventBatchOnBrowserTabClose();
    this.trackUserActivity();
  }

  getSessionData(): { sessionId: string; isFirstSignIn?: boolean; trafficSource?: ENUM_ActivityEventTrafficSource } {
    if (this.isSessionExpired()) {
      return { sessionId: this.startNewSession() };
    }
    return {
      sessionId: localStorage.getItem(SESSION_ID_KEY) || this.startNewSession(),
      isFirstSignIn: localStorage.getItem(SESSION_IS_FIRST_SIGN_IN) === 'true',
      trafficSource: localStorage.getItem(SESSION_TRAFFIC_SOURCE) as ENUM_ActivityEventTrafficSource,
    };
  }

  setIsFirstSignIn(firstSignIn: boolean): void {
    localStorage.setItem(SESSION_IS_FIRST_SIGN_IN, firstSignIn.toString());
  }

  clearSessionData(): void {
    localStorage.removeItem(SESSION_IS_FIRST_SIGN_IN);
    localStorage.removeItem(SESSION_TRAFFIC_SOURCE);

    this.initTrafficSource();
  }

  private initializeSession(): void {
    const storedSession = localStorage.getItem(SESSION_ID_KEY);

    if (!storedSession || this.isSessionExpired()) {
      this.startNewSession();
    }
  }

  private startNewSession(): string {
    const newSessionId = uuid.v4();
    const now = Date.now().toString();

    localStorage.setItem(SESSION_ID_KEY, newSessionId);
    localStorage.setItem(LAST_ACTIVITY_KEY, now);

    this.initTrafficSource();

    return newSessionId;
  }

  private initTrafficSource(): void {
    const emailNotificationClickUrlType = this.activatedRoute.snapshot.queryParams['emailNotificationClickUrlType'];
    if (!this.getTrafficSource()) {
      if (emailNotificationClickUrlType) {
        this.setTrafficSource(ENUM_ActivityEventTrafficSource.ACTIVITY_EVENT_TRAFFIC_SOURCE_EMAIL);
      } else {
        if (environment.mobileApp?.isMobileApp) {
          this.setTrafficSource(ENUM_ActivityEventTrafficSource.ACTIVITY_EVENT_TRAFFIC_SOURCE_MOBILE_APP);
        } else {
          this.setTrafficSource(ENUM_ActivityEventTrafficSource.ACTIVITY_EVENT_TRAFFIC_SOURCE_WEB_LOGIN);
        }
      }
    }
  }

  private isSessionExpired(): boolean {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivity) return true;

    const now = Date.now();
    const elapsed = now - parseInt(lastActivity, 10);

    return elapsed > SESSION_TIMEOUT_MS;
  }

  private refreshActivityTimestamp(): void {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  }

  private registerListenerToSendFinalEventBatchOnBrowserTabClose(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.store.dispatch(forceSentAnalyticsEventsInBuffer());
      }
    });
  }

  private trackUserActivity(): void {
    this.ngZone.runOutsideAngular(() => {
      const activityEvents = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll'];
      merge(...activityEvents.map((event) => fromEvent(document, event)))
        .pipe(throttleTime(1000))
        .subscribe(() => {
          if (this.isSessionExpired()) {
            this.startNewSession();
          } else {
            this.refreshActivityTimestamp();
          }
        });
    });
  }

  private setTrafficSource(trafficSource: ENUM_ActivityEventTrafficSource): void {
    localStorage.setItem(SESSION_TRAFFIC_SOURCE, trafficSource);
  }

  private getTrafficSource(): string {
    return localStorage.getItem(SESSION_TRAFFIC_SOURCE);
  }
}
